import { NavLink, useNavigate } from 'react-router-dom';
import { X, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { ModuleDefinition, ModuleSidebarConfig } from '../modules/ModuleContext';
import { sidebarIconMap } from './sidebarIconMap';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentModule: ModuleDefinition | null;
  sidebarConfig: ModuleSidebarConfig | null;
}

export function MobileDrawer({ isOpen, onClose, currentModule, sidebarConfig }: MobileDrawerProps) {
  const { user, account, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-slate-200 dark:bg-gray-800 text-gray-700 dark:text-white z-50 flex flex-col transition-transform duration-300 ease-in-out md:hidden border-r border-gray-300 dark:border-gray-700 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* User header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-300 dark:border-gray-700">
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex-shrink-0 p-2 hover:bg-slate-300/70 dark:hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Fermer le menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Cabinet name */}
        {account?.name && (
          <div className="px-4 py-2 border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-white/5">
            <p className="text-xs text-gray-500 dark:text-gray-400">{account.name}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-2">
          {sidebarConfig ? (
            sidebarConfig.sections.map((section, idx) => (
              <div key={idx} className="mb-4">
                {section.title && (
                  <h3 className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {section.title}
                  </h3>
                )}
                <ul className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = sidebarIconMap[item.icon];
                    const isModuleBasePath = currentModule && item.href === currentModule.basePath;
                    const allItems = sidebarConfig.sections.flatMap(s => s.items);
                    const isParentOfOther = allItems.some(
                      other => other.href !== item.href && other.href.startsWith(item.href + '/'),
                    );
                    const useEnd = isModuleBasePath || isParentOfOther;

                    return (
                      <li key={item.href}>
                        <NavLink
                          to={item.href}
                          end={useEnd || undefined}
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                            }`
                          }
                        >
                          {Icon && <Icon size={18} />}
                          <span>{item.label}</span>
                          {item.badge !== undefined && item.badge > 0 && (
                            <span className="ml-auto bg-accent-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          ) : (
            // Homepage — no module selected
            <ul className="space-y-0.5">
              <li>
                <NavLink
                  to="/"
                  end
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                    }`
                  }
                >
                  <span>Accueil</span>
                </NavLink>
              </li>
            </ul>
          )}
        </div>

        {/* Footer: Settings + Logout */}
        <div className="border-t border-gray-300 dark:border-gray-700 p-2 space-y-0.5">
          {(currentModule?.id === 'stock' || currentModule?.id === 'zillodocs' || currentModule?.id === 'repondeur' || currentModule?.id === 'equipe') && (
            <NavLink
              to={`/${currentModule.id}/settings`}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
                }`
              }
            >
              <Settings size={18} />
              <span>Paramètres</span>
            </NavLink>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-500 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-200 transition-colors"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default MobileDrawer;
